import {
   useGetActiveLeadSource,
   useGetActiveLeadSourceDetails,
} from '../../hooks'
import CampaignService from '@forwardflow/services/CampaignService'
import { useFubPonds } from '@forwardflow/queries/fub.queries'
import { useAppStore } from '@forwardflow/store'
import { Loader, MultiSelect } from '@mantine/core'
import { message } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

// After the user closes a dropdown we wait this long before saving (no save while it's open).
const SAVE_DELAY_MS = 1000

const EVENT_OPTIONS = [
   { value: 'all', label: 'Any event' },
   { value: 'Seller Inquiry', label: 'Seller Inquiry' },
   { value: 'Property Inquiry', label: 'Property Inquiry' },
   { value: 'General Inquiry', label: 'General Inquiry' },
   { value: 'Viewed Property', label: 'Viewed Property' },
   { value: 'Saved Property', label: 'Saved Property' },
   { value: 'Visited Website', label: 'Visited Website' },
   { value: 'Property Search', label: 'Property Search' },
   { value: 'Saved Property Search', label: 'Saved Property Search' },
   { value: 'Visited Open House', label: 'Visited Open House' },
   { value: 'Viewed Page', label: 'Viewed Page' },
   { value: 'Pond Entered', label: 'Pond Entered' },
]

export const ActivityEventsCard = () => {
   const { title: leadSource } = useParams()
   const { currentOrganization } = useAppStore()

   const { mutate } = useGetActiveLeadSource(leadSource as string)
   // The draft snapshot (draft-if-exists, else live) — so the card reflects unpublished edits.
   const { data: draftDetails, mutate: mutateDraft } =
      useGetActiveLeadSourceDetails(leadSource as string)

   const criteria = draftDetails?.data?.activityCampaignFilterCriteria

   const [isSaving, setIsSaving] = useState(false)
   const [events, setEvents] = useState<string[]>([])
   const [pondIds, setPondIds] = useState<string[]>([])

   // dirty = unsaved local edits (also guards the server -> local sync from clobbering them).
   const dirtyRef = useRef(false)
   const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

   const { data: pondsData } = useFubPonds(
      currentOrganization || '',
      1,
      10000,
      true
   )

   const pondOptions = useMemo(() => {
      const seen = new Set<string>()
      const opts: { value: string; label: string }[] = []
      for (const p of (pondsData || []) as {
         pondId: number
         pondName: string
      }[]) {
         const value = String(p.pondId)
         if (seen.has(value)) continue
         seen.add(value)
         opts.push({ value, label: p.pondName })
      }
      return opts
   }, [pondsData])

   const liveEvents = useMemo(() => criteria?.events ?? [], [criteria])
   const livePondIds = useMemo(
      () =>
         (criteria?.triggers ?? [])
            .filter((t) => t?.type === 'pond')
            .map((t) => String(t.value)),
      [criteria]
   )

   // Sync local state from server whenever the criteria changes, unless the user has a pending edit.
   const liveSignature = `${liveEvents.join('|')}#${livePondIds.join('|')}`
   useEffect(() => {
      if (dirtyRef.current) return
      setEvents(liveEvents)
      setPondIds(livePondIds)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [liveSignature])

   useEffect(() => {
      return () => {
         if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      }
   }, [])

   const saveCriteria = async (nextEvents: string[], nextPondIds: string[]) => {
      setIsSaving(true)
      try {
         await CampaignService.updateActivityFilterCriteria(
            leadSource as string,
            {
               events: nextEvents.length ? nextEvents : ['all'],
               lookback_days: criteria?.lookbackDays ?? null,
               triggers: nextPondIds.map((id) => ({
                  type: 'pond',
                  value: Number(id),
               })),
            }
         )
         await Promise.all([mutate(), mutateDraft()])
      } catch (error) {
         console.error('Failed to update activity filter criteria:', error)
         message.error('Failed to update activity triggers')
      } finally {
         setIsSaving(false)
         dirtyRef.current = false
      }
   }

   // While a dropdown is OPEN the user is still selecting — cancel any pending save.
   const handleDropdownOpen = () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
   }

   // On CLOSE, if there were edits, save after a short delay.
   const handleDropdownClose = () => {
      if (!dirtyRef.current) return
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      saveTimerRef.current = setTimeout(() => {
         void saveCriteria(events, pondIds)
      }, SAVE_DELAY_MS)
   }

   // 'Any event' (all) is exclusive: selecting it clears the rest; selecting any other clears 'all'.
   const handleEventsChange = (next: string[]) => {
      const allJustAdded = next.includes('all') && !events.includes('all')
      const cleaned = allJustAdded
         ? ['all']
         : next.length > 1
            ? next.filter((e) => e !== 'all')
            : next
      setEvents(cleaned)
      dirtyRef.current = true
   }

   const handlePondsChange = (next: string[]) => {
      setPondIds(next)
      dirtyRef.current = true
   }

   return (
      <div className="rounded-lg bg-white/[0.05] p-5">
         <div className="flex items-center justify-between mb-3">
            <p className="text-white/50 text-xs">Activity Triggers</p>
            {isSaving && (
               <span className="flex items-center gap-1.5 text-white/40 text-[10px]">
                  <Loader size={12} color="#9061DA" />
                  Saving…
               </span>
            )}
         </div>

         <div className="space-y-4">
            <div>
               <p className="text-white/40 text-[10px] uppercase tracking-wide mb-2">
                  Events
               </p>
               <MultiSelect
                  data={EVENT_OPTIONS}
                  value={events}
                  onChange={handleEventsChange}
                  onDropdownOpen={handleDropdownOpen}
                  onDropdownClose={handleDropdownClose}
                  searchable
                  clearable
                  placeholder="Select events"
                  nothingFoundMessage="No events"
                  disabled={isSaving}
                  size="xs"
               />
            </div>

            <div>
               <p className="text-white/40 text-[10px] uppercase tracking-wide mb-2">
                  Pond triggers
               </p>
               <MultiSelect
                  data={pondOptions}
                  value={pondIds}
                  onChange={handlePondsChange}
                  onDropdownOpen={handleDropdownOpen}
                  onDropdownClose={handleDropdownClose}
                  searchable
                  clearable
                  placeholder="Select ponds"
                  nothingFoundMessage="No ponds"
                  disabled={isSaving}
                  size="xs"
               />
            </div>
         </div>
      </div>
   )
}
